import * as React from 'react';

import { useQuery } from '@apollo/client';

import { EXTENDED_RANKED_ROLES_LIST } from 'bundles/onboarding-common/data/rankedRolesList';
import GetPaginatedRankedRolesQuery from 'bundles/onboarding-common/queries/GetPaginatedRankedRoles.graphql';
import SearchRolesByNameQuery from 'bundles/onboarding-common/queries/SearchRolesByName.graphql';
import type {
  GetPaginatedRankedRolesQuery as GetPaginatedRankedRolesQueryType,
  GetPaginatedRankedRolesQueryVariables,
} from 'bundles/onboarding-common/queries/__generated__/GetPaginatedRankedRoles';
import type {
  SearchRolesByNameQuery as SearchRolesByNameQueryType,
  SearchRolesByNameQueryVariables,
} from 'bundles/onboarding-common/queries/__generated__/SearchRolesByName';
import type { AvailableRole } from 'bundles/personalization-settings/hooks/useDesiredRolesEditor';

export const SEARCH_DEBOUNCE_MS = 300;
export const CATALOG_LIMIT = 20;

const useDebouncedValue = <T>(value: T, delay: number): T => {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
};

export type UseRolesCatalogSearchResult = {
  availableRoles: AvailableRole[];
  loading: boolean;
};

const useRolesCatalogSearch = (input: string): UseRolesCatalogSearchResult => {
  const searchTerm = useDebouncedValue(input.trim(), SEARCH_DEBOUNCE_MS);
  const hasTerm = searchTerm.length > 0;

  const { data: defaultData, loading: defaultLoading } = useQuery<
    GetPaginatedRankedRolesQueryType,
    GetPaginatedRankedRolesQueryVariables
  >(GetPaginatedRankedRolesQuery, {
    variables: { ids: EXTENDED_RANKED_ROLES_LIST, limit: CATALOG_LIMIT },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    skip: hasTerm,
  });

  const { data: searchData, loading: searchLoading } = useQuery<
    SearchRolesByNameQueryType,
    SearchRolesByNameQueryVariables
  >(SearchRolesByNameQuery, {
    variables: { searchTerm },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    skip: !hasTerm,
  });

  const availableRoles = React.useMemo<AvailableRole[]>(() => {
    if (hasTerm) {
      return (searchData?.PathwaySkillCareerRoles?.searchRolesByName?.elements ?? []).flatMap((role) =>
        role.id && role.name ? [{ id: role.id, name: role.name }] : []
      );
    }
    return (defaultData?.PathwaySkillCareerRoles?.queryRecommendedRolesByIds?.entities?.elements ?? []).flatMap(
      (element) =>
        element?.__typename === 'Skill_CareerRole' && element.id && element.name
          ? [{ id: element.id, name: element.name }]
          : []
    );
  }, [hasTerm, searchData, defaultData]);

  return { availableRoles, loading: hasTerm ? searchLoading : defaultLoading };
};

export default useRolesCatalogSearch;
